import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusPill from "../../../shared/components/StatusPill"

export default function PatientSummaryTab({
  formatMoney,
  formatDateTime,
  patientSummary,
  recentAppointmentsCount,
  upcomingAppointmentsCount,
  recentTreatmentsCount
}) {
  const upcomingAppointments = Array.isArray(patientSummary?.upcomingAppointments)
    ? patientSummary.upcomingAppointments
    : []

  const recentTreatments = Array.isArray(patientSummary?.recentTreatments)
    ? patientSummary.recentTreatments
    : []

  const openAlerts = Number(patientSummary?.summary?.openAlerts || 0)
  const pendingReceivables = Number(patientSummary?.summary?.pendingReceivables || 0)

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <SectionCard title="Resumen" compact>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))",
            gap: 10
          }}
        >
          <CompactMetric label="Citas recientes" value={recentAppointmentsCount} />
          <CompactMetric label="Próximas" value={upcomingAppointmentsCount} tone={upcomingAppointmentsCount > 0 ? "info" : "neutral"} />
          <CompactMetric label="Tratamientos" value={recentTreatmentsCount} tone={recentTreatmentsCount > 0 ? "success" : "neutral"} />
          <CompactMetric label="Alertas" value={openAlerts} tone={openAlerts > 0 ? "warning" : "neutral"} />
          <CompactMetric label="Pendiente" value={formatMoney(pendingReceivables)} tone={pendingReceivables > 0 ? "danger" : "success"} />
        </div>
      </SectionCard>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
          alignItems: "start"
        }}
      >
        <SectionCard title="Próximas citas" compact>
          {!upcomingAppointments.length ? (
            <EmptyStateBlock title="Sin próximas citas" compact />
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {upcomingAppointments.slice(0, 4).map((item, index) => (
                <CompactEventRow
                  key={item.id || index}
                  title={item.reason || item.treatmentName || "Cita programada"}
                  subtitle={`${item.doctorName || "Sin doctor"} · ${formatDateTime(item.startTime)}`}
                  right={
                    <StatusPill tone={appointmentStatusTone(item.status)}>
                      {translateAppointmentStatus(item.status)}
                    </StatusPill>
                  }
                />
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Tratamientos recientes" compact>
          {!recentTreatments.length ? (
            <EmptyStateBlock title="Sin tratamientos recientes" compact />
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {recentTreatments.slice(0, 4).map((item, index) => (
                <CompactEventRow
                  key={item.id || index}
                  title={item.treatmentName || "Tratamiento"}
                  subtitle={`${item.doctorName || "Sin doctor"} · ${formatDateTime(item.createdAt)}`}
                  right={
                    <StatusPill tone="neutral">
                      {formatMoney(item.price || 0)}
                    </StatusPill>
                  }
                />
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  )
}

function CompactMetric({ label, value, tone = "neutral" }) {
  const visual = getToneVisual(tone)

  return (
    <div
      style={{
        borderRadius: 14,
        padding: "10px 12px",
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        display: "grid",
        gap: 4,
        minHeight: 72
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "#64748b",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.25
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 21,
          fontWeight: 900,
          color: visual.color,
          lineHeight: 1.05,
          letterSpacing: "-0.03em"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function CompactEventRow({ title, subtitle, right }) {
  return (
    <div
      style={{
        borderRadius: 14,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        padding: 12,
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap"
      }}
    >
      <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 900,
            color: "#0f172a",
            lineHeight: 1.2,
            wordBreak: "break-word"
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.35 }}>
          {subtitle}
        </div>
      </div>

      {right ? <div>{right}</div> : null}
    </div>
  )
}

function getToneVisual(tone) {
  if (tone === "danger") {
    return { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
  }
  if (tone === "warning") {
    return { bg: "#fff7ed", border: "#fed7aa", color: "#c2410c" }
  }
  if (tone === "success") {
    return { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
  }
  if (tone === "info") {
    return { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
  }
  return { bg: "#f8fafc", border: "#e2e8f0", color: "#0f172a" }
}

function appointmentStatusTone(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (["CONFIRMED", "CHECKED_IN", "COMPLETED", "DONE"].includes(raw)) return "success"
  if (["SCHEDULED", "BOOKED", "PROGRAMMED"].includes(raw)) return "info"
  if (["PENDING", "PARTIAL"].includes(raw)) return "warning"
  if (["CANCELLED", "NO_SHOW"].includes(raw)) return "danger"
  return "neutral"
}

function translateAppointmentStatus(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "CONFIRMED") return "Confirmada"
  if (raw === "CHECKED_IN") return "En atención"
  if (raw === "COMPLETED" || raw === "DONE") return "Realizada"
  if (raw === "SCHEDULED" || raw === "BOOKED" || raw === "PROGRAMMED") return "Programada"
  if (raw === "PENDING") return "Pendiente"
  if (raw === "CANCELLED") return "Cancelada"
  if (raw === "NO_SHOW") return "No asistió"
  if (!raw) return "Sin estado"
  return raw
}
